import React, { useState, useEffect, useCallback } from 'react';
import {
  UtensilsCrossed, Calendar, Coffee, Sun as SunIcon, Cookie, Moon,
  Loader2, ChevronDown, ChevronUp, History, Star, AlertCircle
} from 'lucide-react';
import apiClient from '@/utils/apiClient';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEALS = [
  { key: 'breakfast', label: 'Breakfast', icon: Coffee, color: '#F59E0B', bg: '#FFFBEB', border: '#FDE68A', gradient: 'linear-gradient(135deg, #FEF3C7, #FFFBEB)' },
  { key: 'lunch', label: 'Lunch', icon: SunIcon, color: '#10B981', bg: '#ECFDF5', border: '#A7F3D0', gradient: 'linear-gradient(135deg, #D1FAE5, #ECFDF5)' },
  { key: 'snacks', label: 'Snacks', icon: Cookie, color: '#8B5CF6', bg: '#F5F3FF', border: '#DDD6FE', gradient: 'linear-gradient(135deg, #EDE9FE, #F5F3FF)' },
  { key: 'dinner', label: 'Dinner', icon: Moon, color: '#3B82F6', bg: '#EFF6FF', border: '#BFDBFE', gradient: 'linear-gradient(135deg, #DBEAFE, #EFF6FF)' }
];

const DAY_SHORT = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

const WardenNutrition = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showPastPlans, setShowPastPlans] = useState(false);
  const [pastPlans, setPastPlans] = useState([]);

  useEffect(() => {
    // Set today's day as selected by default
    const today = new Date();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    setSelectedDay(dayNames[today.getDay()]);
  }, []);

  const fetchPlan = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/v1/warden/nutrition/active-plan');
      setPlan(response.data.plan);
    } catch (e) {
      console.error('Failed to fetch nutrition plan:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPastPlans = async () => {
    try {
      const response = await apiClient.get('/v1/warden/nutrition/all-plans');
      const allPlans = response.data.plans || [];
      setPastPlans(allPlans.filter(p => !p.is_active));
    } catch (e) {
      console.error('Failed to fetch past plans:', e);
    }
  };

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" style={{ color: '#4A90D9' }} />
          <p className="text-gray-500">Loading hostel meal plan...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
          <UtensilsCrossed className="w-10 h-10 text-gray-300" />
        </div>
        <h2 className="text-2xl font-bold text-gray-700 mb-2">No Meal Plan Available</h2>
        <p className="text-gray-400">The administration hasn't published a meal plan yet.</p>
      </div>
    );
  }

  const today = new Date();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const todayName = dayNames[today.getDay()];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: '#2C5F7F' }}>
          <UtensilsCrossed className="w-8 h-8" style={{ color: '#4A90D9' }} />
          Nutrition & Meals
        </h1>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-gray-500 font-medium">{plan.week_label}</span>
          <span className="text-gray-300">•</span>
          <span className="text-sm text-gray-400 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {plan.start_date} — {plan.end_date}
          </span>
          <span className="ml-auto px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full uppercase tracking-wider">
            Active Plan
          </span>
        </div>
      </div>

      {/* Day Selector Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
        {DAYS.map(day => {
          const isToday = day === todayName;
          const isSelected = day === selectedDay;
          return (
            <button
              key={day}
              onClick={() => setSelectedDay(day)}
              className={`relative flex-shrink-0 px-6 py-3.5 rounded-2xl font-semibold transition-all text-sm ${
                isSelected
                  ? 'text-white shadow-lg scale-105'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
              style={isSelected ? { background: 'linear-gradient(135deg, #4A90D9, #357ABD)' } : {}}
            >
              <span className="block text-[10px] uppercase tracking-wider opacity-75 mb-0.5">{DAY_SHORT[day]}</span>
              <span className="block">{day}</span>
              {isToday && (
                <span className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full ${isSelected ? 'bg-yellow-300' : 'bg-blue-400'} border-2 border-white`}></span>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected Day's Meals */}
      {selectedDay && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {MEALS.map(meal => {
            const items = plan.meals?.[selectedDay]?.[meal.key] || [];
            const MealIcon = meal.icon;
            return (
              <div key={meal.key} className="bg-white rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group" style={{ borderColor: meal.border }}>
                {/* Meal Header */}
                <div className="px-6 py-4 flex items-center gap-4 border-b" style={{ background: meal.gradient, borderColor: meal.border }}>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm bg-white" style={{ color: meal.color }}>
                    <MealIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg uppercase tracking-tight" style={{ color: meal.color }}>{meal.label}</h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{items.length} item{items.length !== 1 ? 's' : ''} listed</p>
                  </div>
                </div>

                {/* Food Items */}
                <div className="p-6 min-h-[120px]">
                  {items.length > 0 ? (
                    <div className="flex flex-wrap gap-2.5">
                      {items.map((item, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-4 py-2 bg-white rounded-xl text-sm font-semibold border shadow-sm transition-transform hover:scale-105"
                          style={{ color: meal.color, borderColor: meal.border }}
                        >
                          <span className="w-2 h-2 rounded-full mr-2.5" style={{ backgroundColor: meal.color }}></span>
                          {item}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-24 text-gray-300">
                      <AlertCircle className="w-6 h-6 mb-2 opacity-20" />
                      <span className="text-sm font-medium italic">No items listed for this meal</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Week Overview Table */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        <div className="px-8 py-5 border-b bg-gray-50/50 flex items-center justify-between">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Weekly Menu Overview
          </h3>
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Warden Review Mode</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50/30">
                <th className="px-6 py-4 text-left font-bold text-gray-500 text-[10px] uppercase tracking-widest border-b w-24">Day</th>
                {MEALS.map(meal => {
                  const MealIcon = meal.icon;
                  return (
                    <th key={meal.key} className="px-6 py-4 text-left font-bold text-[10px] uppercase tracking-widest border-b" style={{ color: meal.color }}>
                      <div className="flex items-center gap-2">
                        <MealIcon className="w-4 h-4" />
                        {meal.label}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {DAYS.map((day, idx) => {
                const isToday = day === todayName;
                return (
                  <tr
                    key={day}
                    className={`group transition-colors border-b last:border-0 ${
                      isToday ? 'bg-blue-50/40' : idx % 2 === 0 ? 'bg-gray-50/20' : 'bg-white'
                    } hover:bg-blue-50/30 cursor-pointer`}
                    onClick={() => setSelectedDay(day)}
                  >
                    <td className="px-6 py-5">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-800">{DAY_SHORT[day]}</span>
                        {isToday && (
                          <span className="mt-1 w-fit px-1.5 py-0.5 bg-blue-500 text-white rounded text-[8px] font-black uppercase tracking-tighter">TODAY</span>
                        )}
                      </div>
                    </td>
                    {MEALS.map(meal => {
                      const items = plan.meals?.[day]?.[meal.key] || [];
                      return (
                        <td key={meal.key} className="px-6 py-5">
                          <div className="flex flex-wrap gap-1.5">
                            {items.length > 0 ? items.map((item, i) => (
                              <span key={i} className="px-2.5 py-1 rounded-lg text-xs font-bold" style={{ background: meal.bg, color: meal.color, border: `1px solid ${meal.border}` }}>
                                {item}
                              </span>
                            )) : (
                              <span className="text-gray-300 text-xs italic">—</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Past Plans History */}
      <div className="bg-white/50 rounded-2xl p-6 border border-dashed border-gray-200">
        <button
          onClick={() => {
            setShowPastPlans(!showPastPlans);
            if (!showPastPlans && pastPlans.length === 0) fetchPastPlans();
          }}
          className="flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors text-sm"
        >
          <History className="w-4 h-4" />
          <span className="font-bold uppercase tracking-widest text-[10px]">View Past Meal Plans</span>
          {showPastPlans ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showPastPlans && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pastPlans.length > 0 ? pastPlans.map(p => (
              <div key={p.id} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow">
                <h4 className="font-bold text-gray-700 text-sm">{p.week_label}</h4>
                <div className="flex items-center gap-2 mt-2 text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                  <Calendar className="w-3 h-3" />
                  {p.start_date} — {p.end_date}
                </div>
              </div>
            )) : (
              <p className="text-xs text-gray-400 italic">No past plans archived yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WardenNutrition;
